import { DefaultValues, FieldValues, useForm, UseFormReturn } from 'react-hook-form';
import { ZodType } from 'zod';
import zodToJsonSchema from 'zod-to-json-schema';
import { zodResolver } from '@hookform/resolvers/zod';

export interface UseFormProps<T> {
  formSchema: ZodType<any, any, any>;
  formDefaultValues: T;
  schemaTitle: string;
  mode?: 'onBlur' | 'onTouched' | 'onChange' | 'onSubmit';
}

interface ExtendedUseFormReturn<T extends FieldValues> extends UseFormReturn<T> {
  formProperties: any;
  formMethods: UseFormReturn<T>;
}

export const useFormHook = <T extends FieldValues>({
  formSchema,
  formDefaultValues,
  schemaTitle,
  mode = 'onBlur',
}: UseFormProps<T>): ExtendedUseFormReturn<T> => {
  const formMethods = useForm<T>({
    mode,
    resolver: zodResolver(formSchema),
    defaultValues: formDefaultValues as DefaultValues<T>,
  }) as UseFormReturn<T>;

  const jsonSchema = zodToJsonSchema(formSchema, schemaTitle).definitions?.[schemaTitle] as any;

  const formProperties = jsonSchema?.properties;
  return { ...formMethods, formProperties, formMethods };
};

export const createEnum = <T extends string>(keys: T[]): { [K in T]: K } =>
  keys.reduce((acc, key) => {
    acc[key] = key;
    return acc;
  }, Object.create(null));
